<!--
  - Copyright (c) 2023-2024. Haodong JU
  -->

<template>
  <div>
    <div class="py-4 pb-0">
      <v-row>
        <v-col>
          <div class="text-h2 font-weight-black">
            {{ $t('BusinessDataOverview') }}
          </div>
        </v-col>
      </v-row>
      <template v-if="dashboardDataList">
        <v-row>
          <v-col
            v-for="tab in dashboardCard"
            :key="tab.id"
            cols="12"
            lg="4"
            md="6"
            sm="6"
            xl="3"
          >
            <dashboard-card
              :clickable="tab.onClick"
              :color="tab.color"
              :info="tab.detail()"
              :loading="loading"
              :style="!tab.onClick ? '' : 'cursor: pointer;'"
              :subtitle="tab.subtitle"
              :title="tab.name"
              @click="tab.onClick ? tab.onClick() : null"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col
            cols="12"
          >
            <turnover
              :date="$parent.dateRange"
              :label="$t('periodTurnover')"
              :restaurant-list="dashboardDataList"
              class="h-full"
              color="#8c9eff"
              style="height: 100%"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col :class="$vuetify.breakpoint.smAndUp ? 'col-6' : 'col-12'">
            <payment
              :date="$parent.dateRange"
              :info="dashboardDataList"
              class="h-full"
              label="PaymentType"
              style="height: 100%"
            />
          </v-col>
          <v-col :class="$vuetify.breakpoint.smAndUp ? 'col-6' : 'col-12'">
            <dish-rank
              :date="$parent.dateRange"
              :info="dashboardDataList"
              class="h-full"
              label="salesList"
              style="height: 100%"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col :class="$vuetify.breakpoint.smAndUp ? 'col-6' : 'col-12'">
            <inhouse-togo
              :date="$parent.dateRange"
              :info="dashboardDataList"
              :label="$t('staffData')"
              class="h-full"
              style="height: 100%"
            />
          </v-col>
          <v-col :class="$vuetify.breakpoint.smAndUp ? 'col-6' : 'col-12'">
            <cashbook-info
              :date="$parent.dateRange"
              :info="dashboardDataList"
              class="h-full"
              label="cashBook"
              style="height: 100%"
            />
          </v-col>
        </v-row>
      </template>
      <template v-else>
        <v-row>
          <v-col>
            <div class="text-body-1">
              {{ $t('NoDataAvailable') }}
            </div>
          </v-col>
        </v-row>
      </template>
    </div>

    <return-and-discount-dialog
      v-model="showReturnDishDialog"
      :real-info="dashboardDataList"
    />
  </div>
</template>

<script>
import DashboardCard from '@/views/AadenPages/dashboard/components/DashboardCard.vue'
import CashbookInfo from '@/views/AadenPages/dashboard/fragment/CashbookInfo'
import ReturnAndDiscountDialog from '@/views/AadenPages/dashboard/fragment/ReturnAndDiscountDialog.vue'
import Turnover from '@/views/AadenPages/dashboard/fragment/TurnoverDisplay.vue'
import Payment from '@/views/AadenPages/dashboard/fragment/PaymentDisplay.vue'
import DishRank from '@/views/AadenPages/dashboard/fragment/OverviewTaxDisplay.vue'
import InhouseTogo from '@/views/AadenPages/dashboard/fragment/InhouseTogo'
import i18n from '@/i18n'
import GlobalConfig, { Remember, requireOfflineMode } from '@/model/global/GlobalSettings'
import { rateDisplay } from '@/model/utils/utils'
import { getAllStoreInfo } from '@/model/dataLayer/service/MultipleStores'
import { formatPrice } from '@/model/utils/formatPrice'
import store from '@/model/global/store'

export default {
  components: {
    CashbookInfo,
    ReturnAndDiscountDialog,
    DashboardCard,
    Turnover,
    Payment,
    DishRank,
    InhouseTogo
  },
  data: function () {
    return {
      needReload: false,
      dashboardDataList: [],
      selectedAll: { name: this.$t('all') },
      selectedDeviceId: Remember.defaultDeviceIdList,
      showReturnDishDialog: false,
      loading: false
    }
  },
  computed: {
    loginByOffline () {
      return requireOfflineMode()
    },
    dashboardCard () {
      let res = [
        {
          name: i18n.t('SalesData'),
          id: 1,
          color: 'green',
          subtitle: this.$t('SalesDataSubtitle'),
          detail: () => {
            return `${formatPrice(this.dashboardDataList.totalFinishOrder)}
              <span class="text--secondary">( + ${formatPrice(this.dashboardDataList.totalUnpaid)})</span>`
          }
        },
        {
          name: i18n.t('SalesStatistics'),
          id: 2,
          subtitle: this.$t('SalesStatisticsSubtitle'),
          color: 'teal',
          detail: () => {
            return `${formatPrice(this.dashboardDataList.dishTotal)}/
              ${formatPrice(this.dashboardDataList.drinkTotal)}`
          }
        },
        // {
        //   name: i18n.t('profitAnalysis'),
        //   id: 3,
        //   color: 'orange',
        //   subtitle: this.$t('ProfitAnalysisSubtitle'),
        //   detail: () => {
        //     return requireOfflineMode()
        //       ? `<span class="text-caption">${this.$t('LoginRequiredForUsingPlugin')}</span>`
        //       : `<span class="${this.dashboardDataList.newProfit > 0
        //           ? 'lime--text'
        //           : 'red--text'}">${formatPrice(
        //           this.dashboardDataList.newProfit)}</span>`
        //   },
        //   onClick: () => {
        //     router.push({ name: 'RevenueAnalysis' })
        //   }
        // },
        {
          name: i18n.t('PickUpAndDeliveryService'),
          id: 4,
          color: 'indigo',
          subtitle: this.$t('ShopSales'),
          detail: () => {
            return `${formatPrice(this.dashboardDataList.togoTotal)}
              <span class="text--secondary">(
${rateDisplay(this.dashboardDataList.togoTotal / this.dashboardDataList.totalFinishOrder)})</span>`
          }
        },
        {
          name: i18n.t('OrderAmountAveragedCostumerPrice'),
          id: 5,
          color: 'blue',
          subtitle: this.$t('OrderAmountAveragedCostumerPriceSubtitle'),
          detail: () => {
            return `${this.dashboardDataList.orderCount}
              <span class="text--secondary">(
${formatPrice(this.dashboardDataList.totalFinishOrder / this.dashboardDataList.orderCount)})</span>`
          }
        },
        {
          name: this.$t('NumberOfGuests') + '/' + this.$t('PCT'),
          id: 6,
          color: 'amber',
          subtitle: this.$t('NumberOfGuestsHint'),
          detail: () => {
            return `${this.dashboardDataList.guestCount + ' ' + this.$t('Pice') + '/' + formatPrice(
                this.dashboardDataList.avgGuestPrice)}`
          }
        },
        {
          name: i18n.t('DiscountRefund'),
          id: 7,
          color: 'pink',
          onClick: () => {
            this.openReturnDialog()
          },
          subtitle: this.$t('DiscountRefundHint'),
          detail: () => {
            return `
                  ${formatPrice(this.dashboardDataList.discountTotal + this.dashboardDataList.stornoTotal)}`
          }
        },
        {
          name: i18n.t('DishFlotationsRate'),
          id: 8,
          color: 'purple',
          subtitle: this.$t('OrderAmountDetails'),
          detail: () => {
            return `${rateDisplay(this.dashboardDataList.avgTableTurnOverRate / this.selectedDeviceId.length)}<span class = "text--secondary" > (
                ${this.dashboardDataList.inHouseOrderCount})</span>`
          }
        }
      ]
      if (store.state.adminMode === 'supermarket') {
        res = res.filter(it => it.id !== 6 && it.id !== 8)
      }
      return res
    }
  },
  watch: {
    '$parent.dateRange': {
      handler: function (val) {
        this.reloadData()
      }
    }
  },
  async mounted () {
    await this.initialize()
  },
  methods: {
    openReturnDialog () {
      this.showReturnDishDialog = true
    },
    async initialize () {
      this.loading = true
      this.dashboardDataList = []
      this.selectedDeviceId = [GlobalConfig.DeviceId]
      await this.reloadData()
      this.loading = false
    },
    async reloadData () {
      this.loading = true
      try {
        if (this.$parent.dateRange.length > 0) {
          this.dashboardDataList = await getAllStoreInfo(
            this.selectedDeviceId,
            this.$parent.dateRange
          )
        } else {
          this.dashboardDataList = {
            tableTotalCount: 0,
            totalFinishOrder: 0,
            totalUnpaid: 0,
            dishTotal: 0,
            drinkTotal: 0,
            newProfit: 0,
            togoTotal: 0,
            orderCount: 0,
            guestCount: 0,
            avgGuestPrice: 0,
            discountList: [{ deviceId: null, result: [] }],
            stornoList: [{ deviceId: null, result: [] }],
            stornoTotal: 0,
            discountTotal: 0,
            inHouseOrderCount: 0,
            avgTableTurnOverRate: 0,
            smallSelector: 'hour',
            spanTurnover: [],
            paidInfo: [{ deviceId: null, result: [] }],
            taxGroupInfo: [{ deviceId: null, result: [] }],
            total: 0,
            totalTax: 0,
            totalTe: 0,
            collectSalesDate: [{ deviceId: null, result: [] }],
            url: []
          }
        }
        this.loading = false
      } catch (e) {
        console.log(e)
      }
    }
  }
}
</script>
